/**
 * authProvider Refine — копия supply: сессия в httpOnly-cookie, которую JS не
 * видит, поэтому `check` спрашивает сервер (`/api/me`), а не хранилище.
 *
 * dataProvider не нужен: экраны ходят в API через `apiFetch` и react-query.
 */

import type { AuthProvider, DataProvider } from "@refinedev/core";

import { apiFetch, ApiFailure } from "@/lib/api";
import { loginPath, returnPath } from "@/lib/loginReturn";

/** Кто вошёл. Приходит из CRM (`sUser`), своей таблицы пользователей нет. */
export type CurrentUser = { id: number; name: string; short: string };

export const authProvider: AuthProvider = {
  check: async () => {
    // Выход — только на 401. Отказ шлюза (429 `limit_req`), 5xx и обрыв сети —
    // не «никто не вошёл»: прежде `logout: true` на любой сбой гасил живую
    // сессию на сервере. Экран и так под замком — настоящий 401 придёт в onError.
    try {
      await apiFetch<CurrentUser>("/api/me");
      return { authenticated: true };
    } catch (error) {
      if (error instanceof ApiFailure && error.status === 401)
        return { authenticated: false, redirectTo: loginPath(), logout: true };
      return { authenticated: true };
    }
  },

  // Одно поле — пароль CRM: он же называет человека. Логина нет.
  login: async ({ password }: { password: string }) => {
    try {
      await apiFetch<CurrentUser>("/api/login", { method: "POST", body: JSON.stringify({ password }) });
      return { success: true, redirectTo: returnPath() };
    } catch (error) {
      const failure = error instanceof ApiFailure ? error : null;
      return {
        success: false,
        error: { name: failure?.code ?? "LOGIN_FAILED", message: failure?.message ?? "Не удалось войти" },
      };
    }
  },

  logout: async () => {
    // Сессию гасит сервер: иначе украденная cookie продолжала бы работать.
    try {
      await apiFetch<{ ok: boolean }>("/api/logout", { method: "POST" });
    } catch {
      // Даже без ответа сервера уводим на форму входа.
    }
    return { success: true, redirectTo: "/login" };
  },

  getIdentity: async () => {
    // «Никто не вошёл» — только 401; сорвавшийся запрос — ошибка, а не пустота.
    try {
      return await apiFetch<CurrentUser>("/api/me");
    } catch (error) {
      if (error instanceof ApiFailure && error.status === 401) return null;
      throw error;
    }
  },

  onError: async (error) => {
    if (error instanceof ApiFailure && error.status === 401) {
      return { logout: true, redirectTo: loginPath(), error };
    }
    return { error };
  },
};

/** Заглушка: Refine требует dataProvider, экраны его не используют. */
export const dataProvider: DataProvider = {
  getApiUrl: () => "/api",
  getList: async () => {
    throw new Error("dataProvider не используется: экраны ходят через apiFetch");
  },
  getOne: async () => {
    throw new Error("dataProvider не используется");
  },
  create: async () => {
    throw new Error("dataProvider не используется");
  },
  update: async () => {
    throw new Error("dataProvider не используется");
  },
  deleteOne: async () => {
    throw new Error("dataProvider не используется");
  },
};
